import { createRoot, type Root } from "react-dom/client";

import { Node, mergeAttributes } from "@tiptap/core";

import { VideoPlayer } from "simplydo/react/components/VideoPlayer";

import {
  type ResizableMediaOptions,
  createResizableNodeView,
} from "./resizable";

export type VideoOptions = {
  srcRegex?: RegExp;
  resize?: ResizableMediaOptions;
};

declare module "@tiptap/core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Commands<ReturnType> {
    video: { insertVideo: (attrs: { src: string }) => ReturnType };
  }
}

const DEFAULT_MIN_WIDTH = 322;
const DEFAULT_MIN_HEIGHT = 172;

export const Video = Node.create<VideoOptions>({
  name: "video",

  group: "inline",

  inline: true,

  atom: true,

  selectable: true,

  draggable: true,

  addOptions() {
    return {
      resize: {
        enabled: true,
        minWidth: DEFAULT_MIN_WIDTH,
        minHeight: DEFAULT_MIN_HEIGHT,
        preserveAspectRatio: true,
      },
    };
  },

  addAttributes() {
    return {
      src: { default: "" },
      controls: { default: "true" },
      allowfullscreen: { default: "true" },
      width: { default: null },
      height: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: "video",
        priority: 55,
        getAttrs: (element) => {
          const el = element as HTMLVideoElement;
          return {
            src: el.getAttribute("src"),
            controls: el.getAttribute("controls") || "true",
            allowfullscreen: el.getAttribute("allowfullscreen") || "true",
            width: el.getAttribute("width"),
            height: el.getAttribute("height"),
          };
        },
      },
      {
        tag: "span[data-node='video']",
        priority: 60,
        getAttrs: (element) => {
          const video = (element as HTMLElement).querySelector("video");
          if (!video) {
            return false;
          }
          return {
            src: video.getAttribute("src"),
            controls: video.getAttribute("controls") || "true",
            allowfullscreen: video.getAttribute("allowfullscreen") || "true",
            width: video.getAttribute("width"),
            height: video.getAttribute("height"),
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["video", mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const element = document.createElement("div");
      element.className = "richtext-video-node";
      element.style.display = "block";
      element.style.maxWidth = "100%";
      element.style.overflow = "hidden";

      if (node.attrs.width) {
        element.style.width = `${node.attrs.width}px`;
      }
      if (node.attrs.height) {
        element.style.height = `${node.attrs.height}px`;
      }

      let root: Root | null = createRoot(element);
      const renderPlayer = (attrs: typeof node.attrs) => {
        root?.render(
          <VideoPlayer
            src={String(attrs.src ?? "")}
            style={{ width: "100%", height: "100%" }}
          />,
        );
      };
      renderPlayer(node.attrs);

      const destroyRoot = () => {
        root?.unmount();
        root = null;
      };

      const resize = this.options.resize;
      if (!resize?.enabled || typeof document === "undefined") {
        return {
          dom: element,
          update: (updatedNode) => {
            if (updatedNode.type !== node.type) {
              return false;
            }
            renderPlayer(updatedNode.attrs);
            return true;
          },
          destroy: destroyRoot,
        };
      }

      return createResizableNodeView({
        element,
        node,
        editor,
        getPos,
        nodeName: this.name,
        minWidth: resize.minWidth ?? DEFAULT_MIN_WIDTH,
        minHeight: resize.minHeight ?? DEFAULT_MIN_HEIGHT,
        preserveAspectRatio: resize.preserveAspectRatio ?? true,
        onUpdate: (updatedNode) => {
          renderPlayer(updatedNode.attrs);
          return true;
        },
        onDestroy: destroyRoot,
      });
    };
  },

  addCommands() {
    return {
      insertVideo:
        ({ src }) =>
        ({ commands }) => {
          if (!src) {
            return false;
          }
          const { srcRegex } = this.options;
          if (srcRegex && !src.match(srcRegex)) {
            return false;
          }
          return commands.insertContent([
            { type: this.name, attrs: { src } },
            { type: "text", text: " " },
          ]);
        },
    };
  },
});
